/**
 * Victories and Prestige (GDD §10). Every victory has a bar everyone sees
 * and a 5-minute countdown that its rivals can break. Prestige is the shared
 * score: it names the winner of a joint or time victory and ranks everyone
 * when the system collapses. Starting values for tuning.
 */
export const VICTORY_IDS = [
  'domination',
  'economic',
  'technological',
  'joint',
  'time',
  'collapse',
] as const;
export type VictoryId = (typeof VICTORY_IDS)[number];

/** Victories won on a countdown; the rest end the match outright. */
export type CountdownVictoryId = Extract<VictoryId, 'domination' | 'economic' | 'technological'>;

/** Lobby choice (GDD §9): end at the final crisis, or play 15 more minutes after saving the system. */
export const MATCH_MODE_IDS = ['crisisFinale', 'lifeAfterChaos'] as const;
export type MatchModeId = (typeof MATCH_MODE_IDS)[number];

export const VICTORY = {
  /** Countdown before a victory counts: 5 minutes. */
  countdownTicks: 3000,
  /** Economic: the reserve currency and this share of the system's output (decision of 19.10.2026). */
  economicShare: { 2: 0.65, 3: 0.5, 4: 0.5 } as Readonly<Record<number, number>>,
  /** Domination with 3–4 players: more than this share of the Lagrange stations standing, and a vassal. */
  dominationLagrangeShare: 0.5,
  /** «Жизнь после хаоса»: the match goes on this long after the final crisis is beaten. */
  lifeAfterChaosTicks: 9000,
} as const;

/** The megastructure of the technological victory: a Dyson swarm in the corona. */
export const MEGASTRUCTURE = {
  stages: 3,
  /** Each stage takes this long to build... */
  stageTicks: 900,
  /** ...and costs this, paid when it starts. */
  stageCost: { alloys: 100, exotics: 20 },
  /** A hostile fleet holding its hex this long, with no defender there, rolls a stage back. */
  holdTicks: 200,
} as const;

export const PRESTIGE = {
  /** Per economic cycle, shared by output share: the whole system earns this much. */
  economyPerCycle: 0.6,
  /** Per Lagrange station held, per cycle. */
  lagrangePerCycle: 0.2,
  /** Per technology learnt, by tier. */
  techByTier: { 1: 2, 2: 3, 3: 5, final: 10 } as Readonly<Record<1 | 2 | 3 | 'final', number>>,
  /** Per pirate band destroyed, to each player who took part. */
  pirateBand: 5,
} as const;
